/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import BaseDevice from '@avtechsoftware/ra-javascript-utilities/src/classes/devices/BaseDevice';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import SensorListUtils from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/SensorListUtils';
import envConfig from '../../../envConfig';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';

const inventoryDeviceListPromise = RaaAuthenticatedRemoteService.globalInstance().get(
  `${envConfig.apiUrl}/api/v1/device-data/device-list`,
);
const inventorySensorMapPromise = RaaAuthenticatedRemoteService.globalInstance().get(
  `${envConfig.apiUrl}/api/v1/device-data/sensor-map`,
);
const compoundSensorSpecListPromise = RaaAuthenticatedRemoteService.globalInstance().get(
  '/sensors/monitor360/data/compound-sensor-spec-list',
);
// selectors
const selectDeviceListInitialLoading = (state) => state.deviceList.initialLoading;
const selectAllDevices = (state) => state.deviceList.deviceList;
const selectDeviceFromListById = createSelector([
  selectAllDevices, (_, deviceId) => deviceId],
(devices, deviceId) => devices.find((device) => device.id === deviceId));
const selectCompoundSensorSpecList = (state) => state.deviceList.compoundSensorSpecList;
const selectSensorMap = (state) => state.deviceList.deviceListSensorMap;
const selectRoomAlertDeviceList = createSelector(
  selectAllDevices,
  (devices) => {
    if (devices && devices.length > 0) {
      return devices.map((device) => new BaseDevice(
        device.id,
        device.macAddress,
        device.deviceName,
        device.deviceType,
        'MONITOR',
        false,
        device.serialnumber,
        device.capacityJson,
        device.isSuspended,
      ));
    }
    return [];
  },
);
const selectByUuidOrMacAddress = createSelector(
  [selectRoomAlertDeviceList,
    (deviceList, uuid) => uuid],
  (devices, uuidOrMacAddress) => devices.find((device) => device.uuid === uuidOrMacAddress),
);
const selectArchivedDeviceList = createSelector(
  selectRoomAlertDeviceList,
  (devices) => devices.filter(
    (device) => device.isArchived,
  ),
);
const selectNonArchivedDeviceList = createSelector(
  selectRoomAlertDeviceList,
  (devices) => devices.filter(
    (device) => !device.isArchived,
  ),
);

const channelKeyListToSensorList = createSelector(
  [selectRoomAlertDeviceList,
    selectSensorMap,
    (state, channelKeyList) => channelKeyList],
  (deviceList, sensorMap, channelKeyList) => {
    const sensorList = [];
    channelKeyList.forEach((channelKeyItem) => {
      const channelKey = new ChannelKey(channelKeyItem);
      const currDevice = deviceList.find((raDevice) => raDevice.uuid === channelKey.macAddress);
      const detailedSensorInfo = SensorListUtils.getDetailedSensorInfoFromChannelKey(
        channelKey,
        currDevice,
        sensorMap,
      );
      sensorList.push({
        macAddress: channelKey.macAddress,
        channelGroupMacAddress: channelKey.channelGroupMacAddress,
        channelGroupTypeId: channelKey.channelGroupType,
        channelGroupIndex: channelKey.channelGroupIndex,
        label: detailedSensorInfo.sensorLabel,
        portName: detailedSensorInfo.portName,
        sensorName: detailedSensorInfo.sensorName,
      });
    });
    // filter out duplicates created by multiple channels on the same sensor
    const filteredSensorList = sensorList.filter(
      (v, i, a) => a.findIndex((t) => (JSON.stringify(t) === JSON.stringify(v))) === i,
    );
    filteredSensorList.sort(
      (a, b) => a.channelGroupTypeId - b.channelGroupTypeId
          || a.channelGroupIndex - b.channelGroupIndex,
    );
    return filteredSensorList;
  },
);

// thunk
const fetchDeviceListData = createAsyncThunk(
  'deviceList/fetchDeviceListData',
  async (_, { rejectWithValue }) => {
    try {
      return await Promise.all([
        inventoryDeviceListPromise,
        inventorySensorMapPromise,
        compoundSensorSpecListPromise,
      ]);
    } catch (error) {
      return rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data,
      });
    }
  },
);

export const deviceListSlice = createSlice({
  name: 'deviceList',
  initialState: {
    initialLoading: true,
    deviceList: [],
    deviceListSensorMap: [],
    compoundSensorSpecList: [],
    crudStatus: CrudStatus.IDLE,
    crudType: CrudType.READ,
  },
  reducers: {
    clearDeviceListSlice: () => ({
      initialLoading: true,
      deviceList: [],
      deviceListSensorMap: [],
      compoundSensorSpecList: [],
      crudStatus: CrudStatus.IDLE,
      crudType: CrudType.READ,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceListData.pending, (state) => {
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(fetchDeviceListData.fulfilled, (state, action) => {
        const [
          deviceListDataResponse,
          sensorMapDataResponse,
          compoundSensorSpecListResponse,
        ] = action.payload;
        state.deviceList = deviceListDataResponse.deviceList;
        state.deviceListSensorMap = sensorMapDataResponse.sensorMap;
        state.compoundSensorSpecList = compoundSensorSpecListResponse;
        state.crudStatus = CrudStatus.SUCCESS;
        state.initialLoading = false;
      })
      .addCase(fetchDeviceListData.rejected, (state) => {
        state.crudStatus = CrudStatus.ERROR;
        state.initialLoading = false;
      });
  },
});

const { actions, reducer } = deviceListSlice;
export const { clearDeviceListSlice } = actions;
export {
  selectAllDevices,
  selectSensorMap,
  selectCompoundSensorSpecList,
  selectByUuidOrMacAddress,
  selectNonArchivedDeviceList,
  selectRoomAlertDeviceList,
  selectArchivedDeviceList,
  fetchDeviceListData,
  channelKeyListToSensorList,
  selectDeviceListInitialLoading,
  selectDeviceFromListById,
};
export default reducer;
